import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Table } from '@/components/table';
import { useTranslation } from 'react-i18next';
import { EditApplicantStatusModel } from '@/components/process/edit-applicant-process-modal';
import { useBreadcrumbStore } from '@/store/ui';
import {
  ProcessDetailListProps,
  ProcessDetailApplicantProcess
} from '@/types/process.types';
import { Award, Clock, Send } from 'lucide-react';
import { format } from 'date-fns';
import { certificateService } from '@/services/certificate/certificate.service';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import CertificateDisplay from '@/components/certificate/certificate-display';
import { ReleaseAllModal } from '@/components/certificate/release-all-modal';
import ViewSubmittedFormsModal from "./view-submitted-forms-modal";
import DeleteApplicationDialog from "./delete-application-dialog";

const formatListDate = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return isNaN(date.getTime()) ? '—' : format(date, 'MMM dd, yyyy HH:mm');
};

export const ProcessDetailList: React.FC<ProcessDetailListProps> = ({
  data,
  isLoading,
  isError,
  processId,
  title,
  loadingMessage,
  errorMessage,
  noDataMessage,
  baseRoute,
  showEditButton = false,
  showReleaseAll = false,
  showDeleteButton = false,
  showAppliedAt = false,
  showCompletedAt = false,
  users = [],
  meta,
  onPageChange,
  search,
  onSearchChange,
}) => {
  const { t } = useTranslation();
  const [selectedApplicantProcess, setSelectedApplicantProcess] = useState<any | null>(null);
  const [certificatePreviewId, setCertificatePreviewId] = useState<string | null>(null);
  const [submittedFormsId, setSubmittedFormsId] = useState<string | null>(null);
  const [certificatePreviewCanRelease, setCertificatePreviewCanRelease] = useState(false);
  const [releasingAll, setReleasingAll] = useState(false);
  const [releaseModalOpen, setReleaseModalOpen] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState<{ id: string; applicantName: string } | null>(null);
  const [searchInput, setSearchInput] = useState(search ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setCustomBreadcrumbItems } = useBreadcrumbStore();

  useEffect(() => {
    if (search !== undefined) setSearchInput(search);
  }, [search]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleExternalSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearchChange?.(value);
        onPageChange?.(1);
        debounceRef.current = null;
      }, 400);
    },
    [onSearchChange, onPageChange]
  );

  
  // Set breadcrumbs with process name
  useEffect(() => {
    // Try to get process name from the response data
    const processName = (data && 'process' in data) ? data.process.name : 'Unknown Process';
    
    if (processName !== 'Unknown Process') {
      
      // Determine the breadcrumb structure based on the baseRoute
      const isAdminRoute = baseRoute.includes('/admin/management');
      const isStaffRoute = baseRoute.includes('/staff/incoming');
      
      let breadcrumbItems: Array<{ name: string; href: string }> = [];
      
      if (isAdminRoute) {
        // Admin management breadcrumbs
        if (baseRoute.includes('pending-applications')) {
          breadcrumbItems = [
            { name: t("sidebar.management"), href: "" },
            { name: t("processManagement.pendingApplications"), href: "/admin/management/pending-applications" },
            { name: processName, href: "" }
          ];
        } else if (baseRoute.includes('completed-applications')) {
          breadcrumbItems = [
            { name: t("sidebar.management"), href: "" },
            { name: t("processManagement.completedApplications"), href: "/admin/management/completed-applications" },
            { name: processName, href: "" }
          ];
        } else if (baseRoute.includes('disabled-applications')) {
          breadcrumbItems = [
            { name: t("sidebar.management"), href: "" },
            { name: t("processManagement.disabledApplications"), href: "/admin/management/disabled-applications" },
            { name: processName, href: "" }
          ];
        }
      } else if (isStaffRoute) {
        // Staff incoming breadcrumbs
        if (baseRoute.includes('pending-applications')) {
          breadcrumbItems = [
            { name: t("sidebar.incoming"), href: "" },
            { name: t("processManagement.pendingApplications"), href: "/staff/incoming/pending-applications" },
            { name: processName, href: "" }
          ];
        } else if (baseRoute.includes('completed-applications')) {
          breadcrumbItems = [
            { name: t("sidebar.incoming"), href: "" },
            { name: t("processManagement.completedApplications"), href: "/staff/incoming/completed-applications" },
            { name: processName, href: "" }
          ];
        } else if (baseRoute.includes('disabled-applications')) {
          breadcrumbItems = [
            { name: t("sidebar.incoming"), href: "" },
            { name: t("processManagement.disabledApplications"), href: "/staff/incoming/disabled-applications" },
            { name: processName, href: "" }
          ];
        }
      }
      
      if (breadcrumbItems.length > 0) {
        setCustomBreadcrumbItems(breadcrumbItems);
      }
    } else {
      setCustomBreadcrumbItems(null);
    }
  }, [data, baseRoute, t, setCustomBreadcrumbItems]);

  // Cleanup breadcrumbs on unmount
  useEffect(() => {
    return () => {
      setCustomBreadcrumbItems(null);
    };
  }, [setCustomBreadcrumbItems]);

  const formattedApplicantProcesses = useMemo(() => {
    // Handle new backend response structure
    const applicantProcesses = (data && 'applicantProcesses' in data) ? data.applicantProcesses : (Array.isArray(data) ? data : []);
    
    if (!Array.isArray(applicantProcesses)) {
      return [];
    }
    return applicantProcesses
      .filter((_item: ProcessDetailApplicantProcess) => {
        return true;
      })
      .map((item: ProcessDetailApplicantProcess) => {
        let applicantName = 'Unknown';
        if (item.applicant) {
          applicantName = `${item.applicant.firstName || ''} ${item.applicant.lastName || ''}`.trim();
        } else if (users.length > 0) {
          const applicant = users.find((u: any) => u.id === item.applicantId);
          if (applicant) {
            applicantName = `${applicant.firstName} ${applicant.lastName}`;
          }
        }

        // Parse processLevel to get completed and total counts
        let completedFormsCount = 0;
        let totalFormsCount = 0;
        if (item.processLevel && item.processLevel !== 'N/A') {
          const [completed, total] = item.processLevel.split('/').map(Number);
          completedFormsCount = completed;
          totalFormsCount = total;
        }

        return {
          ...item,
          id: item.applicantProcessId,
          service: "Process Applications",
          applicantName,
          processLevel: (
            <span className="flex items-center gap-2">
              {`${completedFormsCount}/${totalFormsCount}`}
              {item.draft && (
                <span
                  className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] whitespace-nowrap"
                  title={`${t('processManagement.draftInProgress')} — ${
                    `${item.draft.savedBy?.firstName ?? ''} ${item.draft.savedBy?.lastName ?? ''}`.trim() ||
                    item.draft.savedBy?.email || ''
                  }`}
                >
                  {t('processManagement.draftInProgress')}
                </span>
              )}
            </span>
          ),
          completedFormsCount,
          totalFormsCount,
          appliedAt: formatListDate(item.appliedAt),
          completedAt: formatListDate(item.completedAt),
          actions: (
            <div className="flex space-x-3 items-center">
              <Link
                to={`${baseRoute}/${processId}/${item.applicantProcessId}`}
                className="text-textIcon"
              >
                <Icon icon="akar-icons:eye" fontSize={18} />
              </Link>
              <button
                type="button"
                onClick={() => setSubmittedFormsId(item.applicantProcessId)}
                className="text-textIcon"
                title={t('processManagement.viewSubmittedForms')}
              >
                <Icon icon="mdi:file-document-multiple-outline" fontSize={18} />
              </button>
              {item.hasCertificate && (
                <button
                  onClick={() => {
                    setCertificatePreviewId(item.applicantProcessId);
                    setCertificatePreviewCanRelease(!!item.hasPendingCertificate || showReleaseAll);
                  }}
                  className={item.hasPendingCertificate ? 'text-yellow-500' : 'text-amber-600'}
                  title={item.hasPendingCertificate ? 'Pending release' : 'View certificate'}
                  type="button"
                >
                  {item.hasPendingCertificate ? <Clock size={18} /> : <Award size={18} />}
                </button>
              )}
              {showEditButton && (
                <button
                  onClick={() => setSelectedApplicantProcess({
                    ...item,
                    processId: processId
                  })}
                  className="text-textIcon"
                >
                  <Icon icon="akar-icons:edit" fontSize={18} />
                </button>
              )}
              {showDeleteButton && (
                <button
                  type="button"
                  onClick={() => setApplicationToDelete({ id: item.applicantProcessId, applicantName })}
                  className="text-red-500"
                  title={t('processManagement.deleteApplication')}
                >
                  <Icon icon="ic:outline-delete" fontSize={18} />
                </button>
              )}
            </div>
          ),
        };
      });
  }, [data, processId, baseRoute, showEditButton, showDeleteButton, users, t]);

  const columns = [
    { key: "applicantName", label: t('processManagement.applicant') },
    { key: "status", label: t('processManagement.status') },
    { key: "processLevel", label: t('processManagement.processLevel') },
    ...(showAppliedAt ? [{ key: "appliedAt", label: t('processManagement.appliedOn') }] : []),
    ...(showCompletedAt ? [{ key: "completedAt", label: t('processManagement.completedOn') }] : []),
    { key: "actions", label: t('processManagement.actions') },
  ];

  const exportData = formattedApplicantProcesses.map((item: any) => ({
    ...item,
    // Override the actions column for export (remove React elements)
    actions: `${item.applicantName} - ${item.status}`,
  }));

  if (isLoading) {
    return <p className="mt-8 text-center text-primary">{loadingMessage}</p>;
  }

  if (isError) {
    return <p className="mt-8 text-center text-red-500">{errorMessage}</p>;
  }

  const displayTitle = (data && 'process' in data) ? data.process.name : title;

  const handleReleaseAll = async () => {
    try {
      setReleasingAll(true);
      const { count } = await certificateService.releaseAllCertificatesForProcess(processId);
      if (count > 0) {
        toast.success(t('certificate.issuanceConfiguration.releaseAllSuccess', { count }));
      } else {
        toast.info(t('certificate.issuanceConfiguration.noUnreleasedCertificates'));
      }
    } catch (error: any) {
      toast.error(t('certificate.issuanceConfiguration.releaseAllFailed'));
      console.error(error);
    } finally {
      setReleasingAll(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-center mb-4 gap-4">
        <h1 className="text-2xl font-semibold text-center">
          {displayTitle}
        </h1>
        {showReleaseAll && (
          <button
            onClick={() => setReleaseModalOpen(true)}
            disabled={releasingAll}
            className="flex items-center gap-2 text-sm px-3 py-1.5 border border-[#001A55]/20 text-[#001A55] bg-[#F6F9FF] hover:bg-[#001A55]/10 rounded-md disabled:opacity-50"
          >
            <Send className={`h-4 w-4 ${releasingAll ? 'animate-pulse' : ''}`} />
            {releasingAll
              ? t('certificate.issuanceConfiguration.releasingAll')
              : t('certificate.issuanceConfiguration.releaseAll')}
          </button>
        )}
      </div>
      {!formattedApplicantProcesses.length && !onSearchChange ? (
        <p className="mt-8 text-center text-gray-600">{noDataMessage}</p>
      ) : (
        <Table
          title={title}
          data={formattedApplicantProcesses}
          columns={columns}
          exportable
          paginate
          pagination={meta ? { page: meta.page, limit: meta.limit, total: meta.total, pages: meta.totalPages } : undefined}
          onPageChange={meta && onPageChange ? onPageChange : undefined}
          externalSearchValue={onSearchChange ? searchInput : undefined}
          onExternalSearchChange={onSearchChange ? handleExternalSearchChange : undefined}
          exportData={exportData}
        />
      )}

      {showDeleteButton && (
        <DeleteApplicationDialog
          application={applicationToDelete}
          onClose={() => setApplicationToDelete(null)}
        />
      )}

      {selectedApplicantProcess && showEditButton && (
        <EditApplicantStatusModel
          isOpen={!!selectedApplicantProcess}
          onClose={() => setSelectedApplicantProcess(null)}
          applicantProcess={selectedApplicantProcess}
        />
      )}

      <ReleaseAllModal
        open={releaseModalOpen}
        onClose={() => setReleaseModalOpen(false)}
        processId={processId}
        onConfirm={async () => {
          await handleReleaseAll();
          setReleaseModalOpen(false);
        }}
        confirming={releasingAll}
      />

      <Dialog open={!!certificatePreviewId} onOpenChange={(open) => { if (!open) { setCertificatePreviewId(null); setCertificatePreviewCanRelease(false); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('certificate.certificateDisplay.title')}</DialogTitle>
          </DialogHeader>
          {certificatePreviewId && (
            <CertificateDisplay applicantProcessId={certificatePreviewId} canRelease={certificatePreviewCanRelease} />
          )}
        </DialogContent>
      </Dialog>
      {submittedFormsId && (
        <ViewSubmittedFormsModal
          isOpen={!!submittedFormsId}
          onClose={() => setSubmittedFormsId(null)}
          applicantProcessId={submittedFormsId}
        />
      )}
      </div>
  );
};

